import { PriceRequestKind } from '@shared/services/price-request-inbox.service';

/**
 * v2's view of the price-request inbox.
 *
 * `PriceRequestInbox` is shared with the v1 screens and hands out two fields
 * of v1 presentation -- `group.icon` is a FontAwesome class and
 * `candidate.statusClass` is a v1 CSS class. v2 is on Lucide and `evo-chip`,
 * and derives both here rather than editing a service two screens still read.
 *
 * Pure functions in a plain module: the mapping is the part worth asserting,
 * and asserting it should not need a TestBed.
 */

/** The tab as it appears in the URL, in the order the page shows them. */
export const V2_PRICE_REQUEST_TABS = ['price-lists', 'handlers'] as const;

export type V2PriceRequestTab = (typeof V2_PRICE_REQUEST_TABS)[number];

/**
 * The URL says `tab`, matching the tariffs screen, and spells its values in
 * kebab case. The service says `kind` and spells them `priceList` -- correct
 * for TypeScript, wrong for a query string.
 *
 * Anything unrecognised is price lists: a stale or hand-edited URL should open
 * the screen, not leave it with no tab selected.
 */
export function tabToKind(tab: string | null | undefined): PriceRequestKind {
  return tab === 'handlers' ? 'handler' : 'priceList';
}

export function kindToTab(kind: PriceRequestKind): V2PriceRequestTab {
  return kind === 'handler' ? 'handlers' : 'price-lists';
}

/**
 * `price_requests/lifecycle.py:17` defines
 * `ATTENTION_STATUSES = (EXPIRING_SOON, EXPIRED, REQUEST_ERROR)` and
 * `needing_attention()` filters to exactly those, so these three are the whole
 * set these endpoints can return.
 *
 * Anything else returns '' rather than a default colour. `WAITING_RESPONSE`
 * exists in the backend and does not reach here; if that ever changes, a plain
 * chip is a better failure than one asserting a severity nobody chose.
 */
export function statusChipClass(status: string): string {
  switch (status) {
    case 'EXPIRED':
    case 'REQUEST_ERROR':
      return 'evo-chip--danger';
    case 'EXPIRING_SOON':
      return 'evo-chip--warn';
    default:
      return '';
  }
}

/**
 * The same three severities, reached from the v1 CSS class instead.
 *
 * `PriceRequestCandidate` exposes `statusLabel` and `statusClass` and not the
 * raw status, so a page holding a candidate cannot call `statusChipClass`.
 * Mapping the v1 class is the honest way round: the alternative is editing
 * `price-request-inbox.service.ts` to expose the raw value, and two v1 screens
 * still read that file.
 *
 * The three inputs are every value `PriceRequestInbox.statusClass` can return.
 */
export function statusChipClassFromV1(statusClass: string): string {
  switch (statusClass) {
    case 'badge-expired':
    case 'badge-error':
      return 'evo-chip--danger';
    case 'badge-expiring':
      return 'evo-chip--warn';
    default:
      return '';
  }
}

/** A key of `V2_ICONS`, as the rail's icons are. */
export function kindIcon(kind: PriceRequestKind): string {
  return kind === 'handler' ? 'lucideLayoutGrid' : 'lucideFileText';
}

/** One record, named for a sentence: "Send requests for 2 price lists?" */
export function kindNoun(kind: PriceRequestKind): string {
  return kind === 'handler' ? 'handler' : 'price list';
}
